/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Icon from '@/components/atoms/icon/icon';
import CalendarSliderTile, {
  CalendarSliderTileProps,
} from '@/components/molecules/calendar-slider-tile/calendar-slider-tile';
import CarouselSlideContent from '@/components/utilities/carousel-slide-content/carousel-slide-content';
import { useI18n } from '@/libraries/contexts/i18n-context';
import validateSameDate from '@/libraries/validators/validate-same-date';
import breakpoints from '@/styles/modules/breakpoints.module.scss';
import styles from './calendar-slider.module.scss';

export interface CalendarSliderProps {
  /** The class name to apply */
  className?: string;
  /** The list of dates to show */
  dates?: CalendarSliderTileProps[];
  /** Indicates whether the calendar slider is in a loading state */
  isLoading?: boolean;
  /** An event that is triggered on change */
  onChange?: (selectedDate: Date, isDisabled: boolean) => void;
  /** The initially selected date */
  selectedDate?: Date;
}

const findDateIndex = (
  isLoading: boolean,
  dates: CalendarSliderTileProps[],
  selectedDate?: Date,
): number => {
  if (isLoading) {
    return Math.round(dates.length);
  }

  return selectedDate
    ? dates.findIndex(
        dateItem =>
          dateItem.date && validateSameDate(dateItem.date, selectedDate),
      )
    : 0;
};

const CalendarSlider = ({
  className,
  dates = [],
  isLoading = false,
  onChange,
  selectedDate,
}: CalendarSliderProps) => {
  const { t } = useI18n();
  const [currentIndex, setCurrentIndex] = useState(
    findDateIndex(isLoading, dates, selectedDate),
  );
  // The date the arrow keys moved focus to, which takes the tab stop with it
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const swiperRef = useRef<SwiperInstance | null>(null);
  const componentRef = useRef<HTMLDivElement>(null);

  // Only one date is in the tab order, so Tab goes in on it and out of the
  // slider with the next press, instead of through every date. It is the date
  // last focused with the arrow keys, otherwise the selected one
  const isValidIndex = (index: number | null): index is number =>
    index !== null && index >= 0 && index < dates.length;
  const tabbableIndex = isValidIndex(focusedIndex)
    ? focusedIndex
    : isValidIndex(currentIndex)
      ? currentIndex
      : 0;

  // The arrow keys move between the dates (Home and End to the first and last),
  // a date is only selected with Enter or Space, so the search doesn't refresh
  // on every step. Focusing a date slides it into view
  useEffect(() => {
    const component = componentRef.current;

    if (!component) return;

    const handleKeydown = (event: KeyboardEvent) => {
      if (!['ArrowLeft', 'ArrowRight', 'End', 'Home'].includes(event.key)) {
        return;
      }

      const tiles = Array.from(
        component.querySelectorAll<HTMLElement>(
          '.swiper-slide a, .swiper-slide button',
        ),
      );
      const index = tiles.indexOf(document.activeElement as HTMLElement);

      if (index === -1) return;

      event.preventDefault();

      const lastIndex = tiles.length - 1;
      const nextIndex = {
        ArrowLeft: Math.max(0, index - 1),
        ArrowRight: Math.min(lastIndex, index + 1),
        End: lastIndex,
        Home: 0,
      }[event.key];

      const targetIndex = nextIndex ?? index;

      setFocusedIndex(targetIndex);
      tiles[targetIndex]?.focus();
    };

    component.addEventListener('keydown', handleKeydown);

    return () => {
      component.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  const handleClickSlide = (
    index: number,
    date: Date,
    isDisabled: boolean = false,
  ) => {
    setCurrentIndex(index);
    setFocusedIndex(null);
    onChange?.(date, isDisabled);
  };

  const handleClickNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const handleClickPrevious = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  useEffect(() => {
    setCurrentIndex(findDateIndex(isLoading, dates, selectedDate));
    setFocusedIndex(null);
  }, [dates, isLoading, selectedDate]);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(currentIndex);
    }
  }, [currentIndex]);

  const renderSlides = () => {
    if (isLoading) {
      return Array.from({ length: 24 }, (_, index) => (
        <SwiperSlide key={index}>
          <CalendarSliderTile isLoading />
        </SwiperSlide>
      ));
    }

    return dates.map((dateItem, dateIndex) => (
      <SwiperSlide key={dateIndex} className={styles.dateItem}>
        {/* Every date can be reached (with the arrow keys) and slides into view on focus */}
        <CarouselSlideContent hideWhenOutOfView={false}>
          <CalendarSliderTile
            {...dateItem}
            active={dateIndex === currentIndex}
            // On the tile instead of the slide, so it is a button the keyboard can use
            onClick={() =>
              dateItem.date &&
              handleClickSlide(
                dateIndex,
                dateItem.date,
                dateItem.disabled as boolean,
              )
            }
            tabIndex={dateIndex === tabbableIndex ? 0 : -1}
          />
        </CarouselSlideContent>
      </SwiperSlide>
    ));
  };

  return (
    <div
      ref={componentRef}
      aria-label={t('items.search.filters.departureDate')}
      className={classNames(styles.component, className)}
      role="group"
    >
      <div className={styles.gradient}>
        <Swiper
          breakpoints={{
            [parseInt(breakpoints.tablet)]: {
              spaceBetween: 12,
            },
          }}
          centeredSlides={true}
          className={styles.swiper}
          initialSlide={currentIndex}
          onSwiper={swiper => (swiperRef.current = swiper)}
          slidesPerView="auto"
          spaceBetween={6}
          speed={500}
          wrapperClass={styles.swiperWrapper}
        >
          {renderSlides()}
        </Swiper>
      </div>

      {/* Pointer-only, keyboard users tab through the dates instead */}
      <button
        aria-hidden="true"
        aria-label={t('items.common.words.previous')}
        className={classNames(styles.button, styles.isPrevious)}
        onClick={handleClickPrevious}
        tabIndex={-1}
        type="button"
      >
        <Icon name="ChevronLeft" size={16} />
      </button>

      <button
        aria-hidden="true"
        aria-label={t('items.common.words.next')}
        className={classNames(styles.button, styles.isNext)}
        onClick={handleClickNext}
        tabIndex={-1}
        type="button"
      >
        <Icon name="ChevronRight" size={16} />
      </button>
    </div>
  );
};

CalendarSlider.displayName = 'CalendarSlider';

export default CalendarSlider;
